import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Chip,
  Grid,
  Paper
} from '@mui/material';
import {
  Person as PersonIcon,
  Download as DownloadIcon,
  ShoppingBag as PurchaseIcon
} from '@mui/icons-material';

interface Purchase {
  id: string;
  productName: string;
  price: number;
  date: string;
  status: 'completed' | 'pending' | 'failed';
}

interface UserProfileProps {
  user: {
    name: string;
    email: string;
    avatar?: string;
    joinDate: string;
  };
  purchases: Purchase[];
  downloads: number;
  onViewPurchases: () => void;
  onViewDownloads: () => void;
}

const UserProfile: React.FC<UserProfileProps> = ({
  user,
  purchases,
  downloads,
  onViewPurchases,
  onViewDownloads
}) => {
  return (
    <Box>
      {/* Profile Header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Avatar
              sx={{ width: 80, height: 80, mr: 3 }}
              src={user.avatar}
            >
              <PersonIcon sx={{ fontSize: 40 }} />
            </Avatar>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h5" gutterBottom>
                {user.name}
              </Typography>
              <Typography variant="body1" color="text.secondary" gutterBottom>
                {user.email}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Member since {new Date(user.joinDate).toLocaleDateString()}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <DownloadIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
            <Typography variant="h4" gutterBottom>
              {downloads}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Downloads
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <PurchaseIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
            <Typography variant="h4" gutterBottom>
              {purchases.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Purchases
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Recent Purchases */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Recent Purchases
            </Typography>
            <Button
              variant="text"
              onClick={onViewPurchases}
            >
              View All
            </Button>
          </Box>
          
          {purchases.length === 0 ? (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
              No purchases yet. Start shopping to see your purchase history here!
            </Typography>
          ) : (
            <List>
              {purchases.slice(0, 5).map((purchase, index) => (
                <React.Fragment key={purchase.id}>
                  <ListItem>
                    <ListItemAvatar>
                      <Avatar>
                        <PurchaseIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={purchase.productName}
                      secondary={`${new Date(purchase.date).toLocaleDateString()} • $${purchase.price.toFixed(2)}`}
                    />
                    <Chip
                      label={purchase.status}
                      color={
                        purchase.status === 'completed' ? 'success' :
                        purchase.status === 'pending' ? 'warning' : 'error'
                      }
                      size="small"
                    />
                  </ListItem>
                  {index < Math.min(purchases.length, 5) - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default UserProfile; 